import React, { useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useMutation } from '@redux-requests/react';
import { useHistory, useParams } from 'react-router-dom';

import Modal from 'shared/components/Modal';
import MultiStepForm from 'shared/components/MultiStepForm';
import LoadingWrapper from 'shared/components/LoadingWrapper';
import { WORKFLOWS_PATH } from 'shared/constants/routePaths';
import { GET_WORKFLOW, EDIT_WORKFLOW } from 'services/workflow/actions';
import { editWorkflow, getWorkflow } from 'services/workflow/actionCreators';
import { getCurrentWorkflow } from 'services/workflow/getters';
import { Workflow } from 'shared/types/workflow';
import { RootState } from '../../index';

import WorkflowDetails from './steps/WorkflowDetails';
import workflowDetailsValidator from './steps/WorkflowDetails/validator';

import SourceSelection from '../common/steps/SourceSelection';
import SourceNavContent from '../common/navigation-content/SourceNavContent';
import sourceValidator from '../common/steps/SourceSelection/validator';

import TransformationSelection from '../common/steps/TransformationSelection';
import TransformationNavContent from '../common/navigation-content/TransformationNavContent';
import transformationValidator from '../common/steps/TransformationSelection/validator';

// It needs to be a custom step because of the way we are handling a change.
// When creating a workflow, the value to be sent to the server will be the destination type,
// but when editing, the data needs to also include the destination GUID.
import DestinationSelection from './steps/DestinationSelection';
import DestinationNavContent from '../common/navigation-content/DestinationNavContent';
import destinationValidator from '../common/steps/DestinationSelection/validator';
import WorkflowDetailsNavContent from '../common/navigation-content/WorkflowDetailsNavContent';

const MODAL_NAME = 'workflow-edit-form';

const MODAL_OVERLAY_CLASS_NAME = 'workflow-edit-form__modal-overlay';
const MODAL_CONTAINER_CLASS_NAME = 'workflow-edit-form__modal-container';
const MODAL_CONTENT_CLASS_NAME = 'workflow-edit-form__modal-content';
const MODAL_CLOSE_ICON_CLASS_NAME = 'workflow-edit-form__modal-close-icon';
const EDIT_FORM_BUTTON_CONTAINER_CLASS_NAME = 'workflow-edit-form__button-container';

const StyledModal = styled(Modal)<{ isGettingWorkflow: boolean }>`
  width: 100%;
  height: 100%;

  & {
    .${MODAL_OVERLAY_CLASS_NAME} {
      padding-right: 0;
      padding-bottom: 0;
      padding-left: 0;
    }

    .${MODAL_CONTAINER_CLASS_NAME} {
      width: 100%;
      height: 100%;
      padding: 0;
    }

    .${MODAL_CLOSE_ICON_CLASS_NAME} {
      top: 3.166666667rem;
      right: 2.333333333rem;
    }

    .${MODAL_CONTENT_CLASS_NAME} {
      display: flex;

      ${({ isGettingWorkflow }) => isGettingWorkflow && css`
        align-items: center;
        justify-content: center;
      `}
    }
  }
`;

const StyledLoadingWrapper = styled(LoadingWrapper)`
  flex: 1;
`;

const StyledMultiStepForm = styled(MultiStepForm)`
  & {
    .${EDIT_FORM_BUTTON_CONTAINER_CLASS_NAME} {
      flex-direction: row-reverse;
    }
  }
`;

const WorkflowEditForm = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { workflowGuid } = useParams<{ workflowGuid: string }>();

  // Cast as Workflow to handle TS presuming workflowToEdit is non-existent as it is initialized null
  const workflowToEdit = useSelector<RootState, Workflow | null>(getCurrentWorkflow);
  const { loading: isGettingWorkflow } = useQuery({ type: GET_WORKFLOW });
  const { loading: isEditingWorkflow } = useMutation({ type: EDIT_WORKFLOW });

  useEffect(() => {
    if (workflowGuid) {
      dispatch(getWorkflow(workflowGuid));
    }
  }, []);

  const handleClose = useCallback(() => {
    history.push(WORKFLOWS_PATH);
  }, []);

  const handleSubmit = useCallback((updatedValues) => {
    // Type check since workflowToEdit can be null
    if (workflowToEdit && workflowToEdit.guid) {
      dispatch(editWorkflow(workflowToEdit, updatedValues));
    }
  }, [workflowToEdit]);

  return (
    <StyledModal
      name={MODAL_NAME}
      isOpen={true}
      onClose={handleClose}
      overlayClassName={MODAL_OVERLAY_CLASS_NAME}
      containerClassName={MODAL_CONTAINER_CLASS_NAME}
      contentClassName={MODAL_CONTENT_CLASS_NAME}
      closeIconClassName={MODAL_CLOSE_ICON_CLASS_NAME}
      isGettingWorkflow={isGettingWorkflow}
    >
      <StyledLoadingWrapper actionToQuery={GET_WORKFLOW}>
        <StyledMultiStepForm
          buttonContainerClassName={EDIT_FORM_BUTTON_CONTAINER_CLASS_NAME}
          steps={[{
            name: 'details',
            component: WorkflowDetails,
            navContentComponent: WorkflowDetailsNavContent,
            initialValues: {
              name: workflowToEdit?.name,
              type: workflowToEdit?.type,
              creationDate: workflowToEdit?.createdAt,
              status: workflowToEdit?.status,
            },
            validator: workflowDetailsValidator,
          }, {
            name: 'sources',
            component: SourceSelection,
            navContentComponent: SourceNavContent,
            initialValues: workflowToEdit?.sources,
            validator: sourceValidator,
          }, {
            name: 'transformations',
            component: TransformationSelection,
            navContentComponent: TransformationNavContent,
            initialValues: workflowToEdit?.transformations,
            validator: transformationValidator,
          }, {
            name: 'destination',
            component: DestinationSelection,
            navContentComponent: DestinationNavContent,
            initialValues: workflowToEdit?.destination,
            validator: destinationValidator,
          }]}
          onSubmit={handleSubmit}
          submitButtonLabel='Save Workflow'
          showBackButton={false}
          showNextButton={false}
          isSubmitting={isEditingWorkflow}
        />
      </StyledLoadingWrapper>
    </StyledModal>
  );
};

export { MODAL_NAME };
export default WorkflowEditForm;
